// Phase 48e: Pure helpers for savings goals.
//
// Mental model (per user direction 2026-08-25):
//   - Each goal has its own allocation_pct (0..100). It receives that
//     percentage of the user's monthly net savings flow:
//       goal_share[m] = monthly_net[m] * allocation_pct / 100
//   - The sum of active goals' pcts may be < 100 (some savings not
//     tracked) or exactly 100 (every dollar tracked). It must NOT
//     exceed 100 (can't double-count). Per-goal cap = 100 alone works
//     as the upper bound since siblings can't push a goal over 100.
//   - `current_override` is the pre-loaded baseline (e.g. money saved
//     before the app was installed). It STACKS on the goal's share.
//   - ETA uses the avg of the last 3 months' GOAL SHARE (not raw net).
//     eta_date_iso = now + eta_months (for display, e.g. "預計 2027-04").
//   - Inactive goals (active = 0) keep their values but don't accumulate.
//   - Goals without `tracking_from` default to "all-time" (every month
//     since the user installed the app is counted).
//
// The function never throws — missing data → 0 progress — so the
// Dashboard widget can render even for half-filled goals.

/**
 * Compute progress for a single goal.
 *
 * @param goal           The goal row (or null/undefined for safety).
 * @param transactions   All non-deleted transactions to scan.
 * @param now            Optional Date for ETA calc. Defaults to new Date().
 * @returns { current_amount, pct, remaining, eta_months, eta_date_iso,
 *            monthly_net, monthly_share, allocation_pct }
 *          - current_amount: monthly_net × pct/100 since tracking_from
 *                            + override.
 *          - pct: current_amount / target_amount.
 *          - remaining: max(0, target - current).
 *          - eta_months: ceil(remaining / avg_share_last_3_months);
 *                        null if avg <= 0 or no history.
 *          - eta_date_iso: 'YYYY-MM' for the projected completion month
 *                          (first day of month + eta_months rounded up).
 *                          null if eta_months is null.
 *          - monthly_net: user's overall monthly net (for UI hint).
 *          - monthly_share: this goal's avg monthly contribution.
 *          - allocation_pct: the goal's allocation % (echoed).
 *
 * Phase 48e: signature simplified — no `allActiveGoals` argument. Each
 * goal's share is independent (monthly_net × pct / 100). The Dashboard
 * widget and the Settings section iterate over all goals and call this
 * once per goal.
 */
export function computeGoalProgress(goal, transactions = [], now = new Date()) {
  if (!goal) return ZERO_PROGRESS;
  const target = Number(goal.target_amount) || 0;
  if (target <= 0) return ZERO_PROGRESS;

  // Inactive (paused) goals keep their values but don't accumulate new
  // progress from monthly net. Short-circuit so they show their
  // historical current_amount without further computation.
  if (goal.active === false) {
    const override = Number(goal.current_override) || 0;
    return {
      ...ZERO_PROGRESS,
      current_amount: Math.max(0, override),
      remaining: Math.max(0, target - Math.max(0, override)),
      allocation_pct: Number(goal.allocation_pct) || 0,
    };
  }

  // Compute the user's overall monthly net (income - expense per month).
  const monthlyNets = computeMonthlyNet(transactions, goal.tracking_from);

  // Each month, this goal's share = monthly_net[m] * pct / 100.
  // Independent of sibling goals — sum of all goals' pcts may be < 100
  // (some savings untracked) or exactly 100 (every dollar tracked).
  const myPct = Number(goal.allocation_pct) || 0;
  const multiplier = myPct / 100;

  // Goal's share per month = monthly_net * multiplier
  const monthlyShares = monthlyNets.map(m => ({ ym: m.ym, share: m.net * multiplier }));
  const cumulative = monthlyShares.reduce((s, m) => s + m.share, 0);

  const override = Number(goal.current_override) || 0;
  const current = Math.max(0, cumulative + override);
  const pct = current / target;
  const remaining = Math.max(0, target - current);

  // ETA based on avg of last 3 months' share. eta_months = 0 means
  // already complete; in that case eta_date_iso is null (no projection).
  const etaMonths = estimateEtaMonths(monthlyShares, current, target);
  const etaDateIso = etaMonths != null && etaMonths > 0
    ? addMonths(now, etaMonths).slice(0, 7)
    : null;

  return {
    current_amount: current,
    pct,
    remaining,
    eta_months: etaMonths,
    eta_date_iso: etaDateIso,
    monthly_net: avgMonthlyNet(monthlyNets, 3),
    monthly_share: avgMonthlyNet(monthlyShares, 3),
    allocation_pct: myPct,
  };
}

const ZERO_PROGRESS = Object.freeze({
  current_amount: 0,
  pct: 0,
  remaining: 0,
  eta_months: null,
  eta_date_iso: null,
  monthly_net: 0,
  monthly_share: 0,
  allocation_pct: 0,
});

/**
 * Bucket transactions into months and return [{ ym, net }] for each
 * month that has at least one transaction, ordered chronologically.
 * `since`: 'YYYY-MM-DD' or null (all-time). Months are calendar months
 * in the local timezone.
 */
export function computeMonthlyNet(transactions, since = null) {
  if (!Array.isArray(transactions)) return [];
  const buckets = new Map();
  for (const t of transactions) {
    if (!t || t.deleted) continue;
    if (!t.occurred_on) continue;
    const ym = t.occurred_on.slice(0, 7);
    if (since && t.occurred_on < since) continue;
    let bucket = buckets.get(ym);
    if (!bucket) {
      bucket = { ym, income: 0, expense: 0, net: 0 };
      buckets.set(ym, bucket);
    }
    const amt = Number(t.amount) || 0;
    if (t.type === 'income') bucket.income += amt;
    else if (t.type === 'expense') bucket.expense += amt;
  }
  if (buckets.size === 0) return [];
  const sortedKeys = [...buckets.keys()].sort();
  const first = sortedKeys[0];
  const last = sortedKeys[sortedKeys.length - 1];
  const result = [];
  let cursor = first;
  while (cursor <= last) {
    const existing = buckets.get(cursor);
    if (existing) {
      existing.net = existing.income - existing.expense;
      result.push(existing);
    } else {
      result.push({ ym: cursor, income: 0, expense: 0, net: 0 });
    }
    cursor = nextMonth(cursor);
  }
  return result;
}

function nextMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Avg over the last `count` months. If fewer months are available,
 * uses what we have.
 */
function avgMonthlyNet(buckets, count) {
  if (!buckets.length) return 0;
  const slice = buckets.slice(-count);
  const sum = slice.reduce((s, m) => s + (m.share != null ? m.share : m.net), 0);
  return sum / slice.length;
}

/**
 * ETA in months at the current avg rate (avg of last 3 months).
 */
function estimateEtaMonths(monthlyShares, current, target) {
  const remaining = target - current;
  if (remaining <= 0) return 0;
  if (monthlyShares.length === 0) return null;
  const last3 = monthlyShares.slice(-3);
  const sum = last3.reduce((s, m) => s + m.share, 0);
  const avg = sum / last3.length;
  if (avg <= 0) return null;
  return Math.ceil(remaining / avg);
}

/**
 * Add `months` to a Date and return 'YYYY-MM-DD' (first of the month
 * that many months out). Negative months also work for past dates.
 */
function addMonths(date, months) {
  const d = new Date(date.getFullYear(), date.getMonth() + months, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Sort goals for Dashboard widget display:
 *   - Active goals first, in-progress (highest pct first), then completed
 *   - Inactive goals at the bottom (paused)
 *   - Stable order: same pct + same status → preserve input order
 */
export function sortGoalsByProgress(goals) {
  return [...goals].sort((a, b) => {
    const aActive = a.active !== false;
    const bActive = b.active !== false;
    // Inactive goals go last.
    if (!aActive && bActive) return 1;
    if (aActive && !bActive) return -1;
    const pa = goalPct(a);
    const pb = goalPct(b);
    // Completed goals go last (within active group).
    if (pa >= 1 && pb < 1) return 1;
    if (pb >= 1 && pa < 1) return -1;
    // In-progress first, higher pct wins.
    if (pa > 0 && pb === 0) return -1;
    if (pb > 0 && pa === 0) return 1;
    return pb - pa;
  });
}

function goalPct(goal) {
  const t = Number(goal?.target_amount) || 0;
  if (t <= 0) return 0;
  return (Number(goal?.current_override) || 0) / t;
}

/**
 * Validate the active goals' allocation_pct. Phase 48e: we allow
 * sum < 100 (some savings untracked) but reject sum > 100 (would
 * double-count). Inactive goals don't count toward the sum.
 *
 * Per-goal cap is enforced at the input layer (number input is
 * clamped to 0..100), so the upper bound on sum is reached when
 * every active goal has pct = 100. Returns an error string or null.
 */
export function validateAllocationPct(goals) {
  const active = (goals || []).filter(g => g && g.active !== false);
  if (active.length === 0) return null;
  const sum = active.reduce((s, g) => s + (Number(g.allocation_pct) || 0), 0);
  // Allow small floating-point drift.
  if (sum > 100.5) {
    return `Active goals 嘅 % 加埋 = ${sum}, 不能多過 100`;
  }
  return null;
}

/**
 * Sum of allocation_pct across active goals. Used by the Settings
 * editor to show the live sum hint. Differs from validateAllocationPct
 * because it returns the number even when under 100.
 */
export function activeAllocationPctSum(goals) {
  return (goals || [])
    .filter(g => g && g.active !== false)
    .reduce((s, g) => s + (Number(g.allocation_pct) || 0), 0);
}